import { ChangeDetectionStrategy, Component, computed, effect, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AnalyticsService } from '../../services/analytics.service';
import { EVENT_DELETED_BUDGET } from '../../analytics/events';
import { injectMutation, injectQueryClient } from '@tanstack/angular-query-experimental';
import { Bucket } from '../../types';
import { GroupService } from '../../services/group.service';
import { MonthService } from '../../services/month.service';
import { BucketService } from '../../services/bucket.service';
import { DrawerService } from '../../services/drawer.service';
import { AsyncPipe } from '@angular/common';
import { BucketSetupToolbarComponent } from '../bucket-setup-toolbar/bucket-setup-toolbar.component';
import { UiButtonDirective } from '../../ui/button/button.directive';
import { UiTabsComponent } from '../../ui/tabs/tabs.component';
import { UiTabComponent } from '../../ui/tabs/tab.component';
import { UiTabLabelDirective } from '../../ui/tabs/tab-label.directive';
import { BucketTransactionListComponent } from '../../shared/bucket-transaction-list/bucket-transaction-list.component';
import { AddTransactionComponent } from '../../transactions/add-transaction/add-transaction.component';
import { TransferBudgetComponent } from '../../bucket/transfer-budget/transfer-budget.component';
import { EditBucketComponent } from '../edit-bucket/edit-bucket.component';
import { SuccessResponse } from '../../generated-api/models';
import { BucketTrendComponent } from '../../reports/bucket-trend/bucket-trend.component';
import { SessionService } from '../../services/session.service';

type BucketTabId = 'transactions' | 'trends' | 'add' | 'exchange' | 'edit';

@Component({
  selector: 'app-bucket',
  templateUrl: './bucket.component.html',
  styleUrl: './bucket.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    BucketSetupToolbarComponent,
    UiButtonDirective,
    UiTabsComponent,
    UiTabComponent,
    UiTabLabelDirective,
    BucketTransactionListComponent,
    AddTransactionComponent,
    TransferBudgetComponent,
    EditBucketComponent,
    BucketTrendComponent,
    AsyncPipe,
  ],
})
export class BucketComponent {
  private groupService = inject(GroupService);
  private bucketService = inject(BucketService);
  month = inject(MonthService);
  private drawer = inject(DrawerService);
  readonly reportsEnabled = toSignal(inject(SessionService).hasEnabledFeature$('reports'), { initialValue: false });
  private readonly pastMonthEditsEnabled = toSignal(
    inject(SessionService).hasEnabledFeature$('past-month-edits'),
    { initialValue: false },
  );

  private readonly queryClient = injectQueryClient();
  private readonly analytics = inject(AnalyticsService);
  readonly deleteBucketMutation = injectMutation(() => this.bucketService.deleteBucketMutationOptions());

  deleting = false;

  readonly bucket = input.required<Bucket>();
  readonly isInDrawer = input(false);
  /** Accounts flagged for history cleanup can also move funds in past months. */
  readonly canTransferInPastMonth = computed(
    () => this.pastMonthEditsEnabled() && this.bucket().monthYear < this.month.monthYearFromDate(),
  );
  readonly initialTab = input<BucketTabId>();
  readonly transactionsLoading = input(false);

  selectedTab: BucketTabId = 'transactions';

  constructor() {
    effect(() => {
      const tab = this.initialTab();
      if (tab) this.selectedTab = tab;
    });
  }

  bucketDeleted() {
    const bucket = this.bucket();
    void this.refreshCategoryPage(bucket.monthYear).then(() => this.drawer.closeDrawer());
  }

  async deleteBucket(): Promise<void> {
    const bucket = this.bucket();
    this.deleting = true;
    await this.deleteBucketMutation.mutateAsync(bucket.id);
    this.analytics.sendEvent(EVENT_DELETED_BUDGET);
    await this.refreshCategoryPage(bucket.monthYear);
    this.drawer.closeDrawer();
  }

  update({ success }: Partial<SuccessResponse> = {}): void {
    const bucket = this.bucket();
    if (success) this.selectedTab = 'transactions';

    void this.refreshCategoryPage(bucket.monthYear).then(() => this.refreshBucketDetail(bucket));
  }

  private refreshCategoryPage(monthYear: string): Promise<unknown> {
    return this.queryClient.fetchQuery(this.groupService.groupPageQueryOptions(monthYear));
  }

  private refreshBucketDetail(bucket: Bucket): Promise<Bucket> {
    return this.queryClient.fetchQuery(this.bucketService.bucketDetailQueryOptions(bucket.id, bucket.monthYear));
  }
}
